/**
 * 集計表示 (BG 全体×配下複数) で参照列を「採用」する時の取込対象の振り分け
 * (spec AC-DFD-047d / 047e / 048、issue #7436)。
 *
 * 過去実績 (各小売に帰属する実績) と確定履歴 (確定時に小売別に保存された値)
 * のどちらも、呼び出し元 (index.tsx) が小売ごとの 48 コマ値を解決して渡す。
 * 本関数は現在の BG 配下の小売を基準に、
 *
 * - 値を 1 コマ以上持つ小売 → 取込対象 (`targets`。値はそのまま入れ、按分しない)
 * - 値が 1 コマも無い小売 (解決結果に無い小売を含む) → 取り込まずに通知する小売
 *   (`missingDisplayNames`、AC-DFD-047e / 048 の「{内訳名} は … 取り込みませんでした」)
 *
 * に分ける。解決結果にあっても現在の BG 配下にいない小売 (確定時にいて今はいない
 * 小売) は対象にも通知にも含めない (AC-DFD-032)。順序は現在の BG 配下の並び順。
 */
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

export type AggregateAdoptRetailer = {
  retailerBusinessCd: string
  displayName: string
}

export type RetailerSlots = {
  retailerBusinessCd: string
  slots: KomaSlots
}

export type AggregateAdoptTargets = {
  targets: RetailerSlots[]
  missingDisplayNames: string[]
}

export function splitAggregateAdoptTargets(
  currentRetailers: AggregateAdoptRetailer[],
  resolved: RetailerSlots[],
): AggregateAdoptTargets {
  const byCd = new Map(resolved.map((r) => [r.retailerBusinessCd, r.slots]))
  const targets: RetailerSlots[] = []
  const missingDisplayNames: string[] = []
  for (const retailer of currentRetailers) {
    const slots = byCd.get(retailer.retailerBusinessCd)
    if (slots !== undefined && slots.some((v) => v !== undefined)) {
      targets.push({ retailerBusinessCd: retailer.retailerBusinessCd, slots })
    } else {
      missingDisplayNames.push(retailer.displayName)
    }
  }
  return { targets, missingDisplayNames }
}

/**
 * 確定履歴の集計採用で内訳「帰属不明」に入れる値 (AC-DFD-047c / 048)。確定値に
 * 帰属不明は無い (AC-DFD-058) ため 48 コマとも 0。
 */
export const CONFIRMED_UNATTRIBUTED_ADOPT_SLOTS: KomaSlots = Array.from(
  { length: 48 },
  () => 0,
)

/**
 * 過去実績の集計採用で内訳「帰属不明」に入れる値 (AC-DFD-047c / 047d)。`-UNK` の
 * 実績をそのまま入れ、実績のあるコマで `-UNK` が無ければ 0 にする (帰属不明が
 * 0 なだけで取り逃がしではない)。実績そのものが無いコマ (エリア合計が未取得) は
 * 入れない (undefined = 採用で変えない)。
 */
export function pastActualUnattributedAdoptSlots(
  areaSlots: KomaSlots,
  unattributedSlots: KomaSlots | undefined,
): KomaSlots {
  return areaSlots.map((area, i) =>
    area === undefined ? undefined : (unattributedSlots?.[i] ?? 0),
  )
}
